import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useVehicleQuery } from 'src/api/queries'
import { AddressLink } from 'src/components/AddressLink'
import CloseButton from 'src/components/CloseButton'
import CodeTag from 'src/components/CodeTag'
import Iconify from 'src/components/Iconify'
import KeyValueList from 'src/components/KeyValueList'
import KeyValueRow from 'src/components/KeyValueRow'
import VehicleImage from 'src/components/VehicleImage'
import TelemetryRow from 'src/components/vehicle/TelemetryRow'
import VehicleCommands from 'src/components/vehicle/VehicleCommands'
import VehicleStatusSwitch from 'src/components/vehicle/VehicleStatusSwitch'
import { rentalAvailability } from 'src/components/vehicle/vehicleRentalAvailability'
import { VehiclesQuery } from 'src/generated'
import useResponsive from 'src/hooks/useResponsive'
import { currentProviderFreestyleRentalsEnabled } from 'src/redux/providers/selectors'
import { useSelector } from 'src/redux/store'
import { getModelName } from 'src/utils'

import { vehicleDisplayName } from 'src/format'
import { CARD_EDGE_WIDTH } from 'src/theme/overrides/Card'
import radii from 'src/theme/radii'
import { layout } from 'src/theme/tokens.generated'
import { VehicleStatusChip } from '../../VehicleListPage/parts'
import { MAP_LAYER } from './consts'

type MapVehicle = VehiclesQuery['vehicles']['vehicles'][0]

type MapVehicleSheetProps = {
  mapVehicle: MapVehicle
  onClose: () => void
}

const SheetBody = ({ mapVehicle, onClose }: MapVehicleSheetProps) => {
  const navigate = useNavigate()
  const freestyleEnabled = useSelector(currentProviderFreestyleRentalsEnabled)
  const { vehicle, loading, refetch } = useVehicleQuery(mapVehicle.id)
  const status = mapVehicle.latestStatus
  // The model and the year it was built, in the one form the record header
  // already uses: `Model Y Performance · 2022`. The callout said the model
  // alone, so the same car read as two different facts depending on which
  // screen you were on, and the year is what tells two otherwise identical
  // cars in a fleet apart. `filter(Boolean)` because a vehicle imported
  // without a year has none, and `Model Y Performance · ` is worse than the
  // model on its own.
  const model = [getModelName(mapVehicle), mapVehicle.modelYear]
    .filter(Boolean)
    .join(' · ')
  const linkTo = status?.rentalId
    ? `/admin/rentals/${status.rentalId}`
    : `/admin/vehicles/${mapVehicle.id}`

  const isSynced = !!vehicle?.latestStatus
  // Null while the detail query is in flight, and null for a car that has
  // never reported. Neither is "off", so neither draws a switch.
  const freestyleAvailable = vehicle
    ? rentalAvailability(vehicle).freestyle
    : null

  return (
    <>
      {/* The list record already carries the link, so the callout draws the
          picture the moment it opens rather than flashing an empty state while
          the detail query runs. `flush` because the card clips the corners.

          The dismiss sits on the picture rather than beside the name. It is
          the callout's own control and not one of the vehicle's, and down in
          the header row it shared an edge with the status chip, so the
          control that closes the panel was one target away from the only
          thing in that row that looks like a control. On the picture it is in
          the corner a panel is dismissed from, and nothing is beside it. */}
      <Box sx={{ position: 'relative' }}>
        <VehicleImage
          picture={mapVehicle.picture}
          alt={[mapVehicle.name, model].filter(Boolean).join(', ')}
          place="callout"
          flush
        />
        <CloseButton
          onClick={onClose}
          sx={{
            'position': 'absolute',
            // `top` and `right` are not spacing props, so a bare number here
            // is pixels: read through `theme.spacing`, the way the map's own
            // floating furniture does.
            'top': (theme) => theme.spacing(1),
            'right': (theme) => theme.spacing(1),
            // It sits on a picture this component does not choose, so it
            // carries its own ground rather than trusting the pixels under
            // it. The surface and its hover are the theme's, so the mark
            // keeps the console's contrast in both schemes.
            //
            // **The sunken neutral, not paper.** The frame behind it letterboxes
            // on paper now, so a paper button on a white render was a shadow
            // with a glyph floating in it: the control had no edge of its own
            // and the only thing separating it from the car was an elevation
            // cue. The sunken fill is the ground this console already uses for
            // a control that has to be found on a surface it does not own, and
            // the hover still lifts off it.
            'bgcolor': 'background.neutral',
            'boxShadow': (theme) => theme.customShadows.z8,
            '&:hover': {
              bgcolor: (theme) => theme.hover.surface,
            },
          }}
        />
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          {/* The plate is beside the name, not under it. They are one
              identity (the name an operator uses and the plate they read off
              the car) and stacking them pushed the model, the address and
              the charge down a row each for no reading gained. It wraps
              rather than truncating, because a plate half shown is a plate
              nobody can match against a car. */}
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              sx={{ columnGap: 1, rowGap: 0.5 }}
            >
              {/* The name gives up the plate when it carries it, and the
                  tag keeps it: the tag is the copy an operator matches
                  against a windscreen. */}
              {vehicleDisplayName(mapVehicle.name, mapVehicle.reg) ? (
                <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
                  {vehicleDisplayName(mapVehicle.name, mapVehicle.reg)}
                </Typography>
              ) : null}
              {mapVehicle.reg ? <CodeTag>{mapVehicle.reg}</CodeTag> : null}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              display="block"
              sx={{ mt: 0.5 }}
            >
              {model}
            </Typography>
          </Box>
          {/* Off hire from the list record rather than the detail query, so
              the chip is right the moment the callout opens. Without it the
              chip read the freestyle flag alone, and a car anybody could book
              said `Disabled` here. */}
          {status && (
            <VehicleStatusChip status={status} offHire={mapVehicle.disabled} />
          )}
        </Stack>

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {status?.location && (
            // Where the car is, as a way of getting to it. Somebody reading
            // this is about to drive there, and the address was a line of
            // text they had to retype into a phone. The same link the rest of
            // the console uses, so the destination and the tooltip are stated
            // once.
            // The pin is `control` and the address is primary ink, so the
            // address and the charge under it read as two facts of the same
            // weight rather than as a heading over a caption. They are: both
            // are what the car last said about itself.
            <Stack direction="row" alignItems="center" sx={{ gap: 1.25 }}>
              <Iconify
                size="control"
                icon="lucide:map-pin"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              />
              <AddressLink
                location={status.location}
                variant="body2"
                color="text.primary"
                underline="hover"
              />
            </Stack>
          )}
          {/* What the car last reported and when it said it, on one line
              with the control that refetches both.

              This is also where the marker's dashed ring went. On the map that
              signal fired on nearly every car (the telemetry policy is minutes
              and a parked fleet reports in hours), so it drew an outline round
              Available and Disabled alike and read as chrome. Here it is on
              the panel an operator opens before doing anything to the car, it
              says the age in words, and only the one state worth acting on
              takes a colour.

              It shipped once as a battery, a chip reading `Refreshed 7m ago`
              and a bare glyph. The chip was the mistake: that primitive means
              a domain and a state from a closed vocabulary, and an age is
              metadata about a reading that is different a second later. At
              chip weight it competed with the availability chip in the header,
              which is a real status. */}
          <TelemetryRow
            vehicleId={mapVehicle.id}
            batteryLevel={status?.batteryLevel}
            updatedAt={status?.updatedAt}
            onRefreshed={refetch}
          />

          {/* **Whether the car is on hire, under what it reports.** It used to
              be the last row on the card, below the command dial, which put
              the one switch on this panel a scroll away from the readings it
              belongs with. Charge, position, age and availability are the four
              things somebody opens a pin to check, and the fourth was under a
              fold on a 380px card.

              The composition, not half of it. Reading the status flag on its
              own drew `Enabled` for a car already off hire, which every quote
              endpoint refuses: the same defect the fleet list's availability
              column exists to stop telling. It is null while the detail query
              is in flight, so the row arrives with the rest of the detail
              rather than flashing a switch in a state nobody has read yet. */}
          {freestyleEnabled && freestyleAvailable !== null && vehicle && (
            // **Inside a list, and the list is what stops the label
            // wrapping.** A bare row's label track is the fixed 110px floor,
            // which `Enabled for Freestyle` broke onto two lines against, with
            // a switch beside it and half the panel empty to its right.
            // Widening the floor for every lone row in the console was the
            // wrong fix: nine cards here draw several bare rows as siblings,
            // and each would then take its own label width and lose the one
            // left edge the row exists to give them. A list sizes its track
            // to the labels *in this card*, which is exactly the question
            // being asked, and 45% of a 380px callout is room for this label
            // on one line.
            //
            // `KeyValueRow` rather than the `InfoRow` shim, because this row
            // needs `trailing` and new code writes the primitive anyway.
            // Label left, control right: this is a settings row in a narrow
            // panel with no sibling row to break an edge with.
            <KeyValueList>
              <KeyValueRow
                icon="lucide:power"
                label="Enabled for Freestyle"
                trailing
                value={
                  <VehicleStatusSwitch
                    vehicle={vehicle}
                    value={freestyleAvailable}
                    onSuccess={refetch}
                  />
                }
              />
            </KeyValueList>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {loading && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {!loading && !isSynced && (
          <Alert severity="warning">
            This vehicle isn&apos;t synced with Tesla yet.
          </Alert>
        )}

        {!loading && isSynced && vehicle && (
          <VehicleCommands
            vehicle={vehicle}
            place={status?.location?.address?.displayString}
            refetch={refetch}
          />
        )}

        <Button
          fullWidth
          // Navigation, not a commit. Enable drive is the one bold control in
          // this callout and it is the ink command inside the dial.
          variant="outlined"
          sx={{ mt: 2 }}
          startIcon={<Iconify icon="lucide:arrow-up-right" />}
          onClick={() => navigate(linkTo)}
        >
          View details
        </Button>
      </Box>
    </>
  )
}

export default function MapVehicleSheet({
  mapVehicle,
  onClose,
}: MapVehicleSheetProps) {
  const isDesktop = useResponsive('up', 'md')

  // **Escape closes the callout.**
  //
  // The phone branch has always had it and nobody wrote a line for it: a
  // `Drawer` is a MUI `Modal`, and a modal closes on Escape. The desktop
  // branch is an absolutely positioned `Card` (not a modal, because the map
  // behind it stays live and pannable while it is open), so it inherited
  // nothing, and the only way out of it was the one button in its corner.
  //
  // Bound on the desktop branch alone. The `Drawer` calls `onClose` itself, so
  // a second listener there would close it twice on one press, which is
  // harmless today and is the kind of thing that stops being harmless when
  // `onClose` grows a side effect.
  //
  // `keydown` on the document rather than on the card: nothing inside the
  // callout holds focus when it opens (it is opened by clicking a pin on the
  // map), so a handler on the element would never see the key.
  useEffect(() => {
    if (!isDesktop) {
      return undefined
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isDesktop, onClose])

  // **The content states the width, and the card sizes to it.** The card is
  // the one scroller here, and where scrollbars take room (Windows, or a Mac
  // set to always show them) a card of a fixed width gives the bar's 11 to
  // 15px to its content: the command dial's row was 314px with the bar
  // hidden and 299 with it shown, four 72px cells need 312, and Windows drew
  // the dial three to a row. An absolutely placed card with no width of its
  // own is as wide as its content plus its scrollbar, so the bar goes beside
  // the content instead. `scrollbar-gutter: stable` holds the room whether or
  // not the readings overflow yet, so the card does not change width when the
  // vehicle's detail lands. With overlay scrollbars both are no-ops and the
  // card is the token's width, as it was.
  if (isDesktop) {
    return (
      <Card
        data-map-callout
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          maxHeight: 'calc(100% - 32px)',
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          // Over the map's furniture: the refresh mark stood on its close
          // button.
          zIndex: MAP_LAYER.callout,
          boxShadow: (theme) => theme.customShadows.z8,
        }}
      >
        <Box sx={{ width: layout.mapCalloutWidth - 2 * CARD_EDGE_WIDTH }}>
          <SheetBody mapVehicle={mapVehicle} onClose={onClose} />
        </Box>
      </Card>
    )
  }

  return (
    <Drawer
      anchor="bottom"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '85vh',
          borderTopLeftRadius: radii.surface,
          borderTopRightRadius: radii.surface,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.25 }}>
        <Box
          sx={{
            width: 38,
            height: 4,
            borderRadius: radii.pill,
            bgcolor: 'divider',
          }}
        />
      </Box>
      <SheetBody mapVehicle={mapVehicle} onClose={onClose} />
    </Drawer>
  )
}
